import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CommuteOutlinedIcon from '@mui/icons-material/CommuteOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { UseNotificationsResult } from '../../Hooks/useNotifications';
import { NotificationItem } from '../../Repositories/NotificationRepository';
import { resolveNotificationRoute } from '../../config/NOTIFICATION_ROUTES';
import useTailwindBrand from '../../Theme/useTailwindBrand';
import { capitalizeFirstLetter } from '../../Utils/Function';
import { groupNotifications, notificationCategoryLabel } from '../../Utils/notificationGroups';
import 'moment/locale/fr';

/** Icône par catégorie, cloche générique pour une catégorie inconnue. */
export const CATEGORY_ICONS: Record<string, React.ElementType> = {
  declaration: EventNoteOutlinedIcon,
  travel: CommuteOutlinedIcon,
  billing: ReceiptLongOutlinedIcon,
  client: PeopleOutlineOutlinedIcon,
  account: ManageAccountsOutlinedIcon,
};

export const categoryIcon = (category: string): React.ElementType => CATEGORY_ICONS[category] ?? NotificationsNoneOutlinedIcon;

export const formatRelativeDate = (date: string) => {
  const value = moment(date);
  return value.isValid() ? capitalizeFirstLetter(value.locale('fr').fromNow()) : '';
};

/** Rôle courant, lu comme dans RoleView : la destination « documents » en dépend. */
export const useIsClient = (): boolean => useSelector((state: any) => state?.user?.currentUser?.isClient) || false;

interface NotificationFilterChipsProps {
  notifications: UseNotificationsResult;
}

/**
 * Puces de filtre : « Toutes » / « Non lues » (état de lecture) puis une puce par
 * catégorie rencontrée (dimension indépendante, cliquer la puce active la retire).
 */
export const NotificationFilterChips: React.FC<NotificationFilterChipsProps> = ({ notifications }) => {
  const { brandConfig } = useTailwindBrand();
  const { filters, categories, setFilters } = notifications;
  const primary = brandConfig.colors.primary;

  const chipSx = (active: boolean) => ({
    height: 26,
    fontSize: '0.75rem',
    fontWeight: 600,
    fontFamily: brandConfig.fontFamily,
    borderRadius: '13px',
    color: active ? '#fff' : '#374151',
    backgroundColor: active ? primary : 'transparent',
    borderColor: active ? primary : 'rgba(0,0,0,0.12)',
    '&:hover': { backgroundColor: active ? primary : `${primary}10` },
    '&.MuiChip-clickable:hover': { backgroundColor: active ? primary : `${primary}10` },
  });

  return (
    <Box
      role="group"
      aria-label="Filtrer les notifications"
      sx={{ px: 2, py: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75, flexShrink: 0 }}
    >
      <Chip
        label="Toutes"
        size="small"
        variant={filters.unreadOnly ? 'outlined' : 'filled'}
        aria-pressed={!filters.unreadOnly}
        onClick={() => setFilters({ unreadOnly: false })}
        sx={chipSx(!filters.unreadOnly)}
      />
      <Chip
        label="Non lues"
        size="small"
        variant={filters.unreadOnly ? 'filled' : 'outlined'}
        aria-pressed={filters.unreadOnly}
        onClick={() => setFilters({ unreadOnly: true })}
        sx={chipSx(filters.unreadOnly)}
      />
      {categories.map((category) => {
        const active = filters.category === category;
        return (
          <Chip
            key={category}
            label={notificationCategoryLabel(category)}
            size="small"
            variant={active ? 'filled' : 'outlined'}
            aria-pressed={active}
            onClick={() => setFilters({ category: active ? null : category })}
            sx={chipSx(active)}
          />
        );
      })}
    </Box>
  );
};

interface NotificationListProps {
  /** État partagé fourni par useNotifications (cloche, page). */
  notifications: UseNotificationsResult;
  /** Appelé après une navigation réussie (le panneau se ferme). */
  onNavigate?: () => void;
  /** Puces de filtre au-dessus de la liste. */
  showFilters?: boolean;
}

/**
 * Liste des notifications, partagée par le panneau de la cloche et la page dédiée :
 * puces de filtre, groupes par ancienneté (aujourd'hui, cette semaine, plus ancien),
 * lignes avec état non lu, bordure gauche pour les priorités hautes, bouton « Masquer »
 * au survol, « Voir plus ».
 */
const NotificationList: React.FC<NotificationListProps> = ({ notifications, onNavigate, showFilters = true }) => {
  const { brandConfig } = useTailwindBrand();
  const navigate = useNavigate();
  const isClient = useIsClient();
  const {
    items,
    filters,
    loaded,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    markAsRead,
    hide,
  } = notifications;
  const primary = brandConfig.colors.primary;
  const groups = useMemo(() => groupNotifications(items), [items]);
  const isFiltered = filters.unreadOnly || !!filters.category;

  const handleItemClick = (item: NotificationItem) => {
    markAsRead(item);
    const destination = resolveNotificationRoute(item.target, isClient);
    // pas de destination connue : la notification est seulement marquée comme lue
    if (!destination) return;
    onNavigate?.();
    navigate(destination.path, destination.state ? { state: destination.state } : undefined);
  };

  const handleHide = (event: React.MouseEvent, item: NotificationItem) => {
    // le bouton est hors du ListItemButton, mais on protège quand même la ligne
    event.stopPropagation();
    event.preventDefault();
    hide(item);
  };

  const renderMessage = (title: string, text: string, Icon: React.ElementType) => (
    <Box sx={{ px: 3, py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1 }}>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: primary,
          backgroundColor: `${primary}12`,
          mb: 0.5,
        }}
      >
        <Icon />
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#111827' }}>{title}</Typography>
      <Typography sx={{ fontSize: '0.8rem', color: '#6B7280', lineHeight: 1.5 }}>{text}</Typography>
    </Box>
  );

  const renderItem = (item: NotificationItem) => {
    const Icon = categoryIcon(item.category);
    const isHigh = item.priority === 'high';
    const accent = isHigh ? brandConfig.colors.error : primary;

    return (
      <ListItem
        key={item.id}
        disablePadding
        sx={{
          borderBottom: '1px solid rgba(0,0,0,0.05)',
          // « Masquer » discret : visible au survol de la ligne ou au focus clavier
          '& .notification-hide': { opacity: { xs: 1, sm: 0 }, transition: 'opacity 120ms' },
          '&:hover .notification-hide, &:focus-within .notification-hide': { opacity: 1 },
        }}
        secondaryAction={(
          <Tooltip title="Masquer" arrow>
            <IconButton
              edge="end"
              size="small"
              className="notification-hide"
              aria-label={`Masquer la notification : ${item.title}`}
              onClick={(event) => handleHide(event, item)}
              sx={{ color: '#9CA3AF', '&:hover': { color: '#374151', backgroundColor: 'rgba(0,0,0,0.05)' } }}
            >
              <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      >
        <ListItemButton
          alignItems="flex-start"
          onClick={() => handleItemClick(item)}
          sx={{
            pl: isHigh ? '13px' : 2,
            pr: 6,
            py: 1.5,
            gap: 1.5,
            borderLeft: `3px solid ${isHigh ? brandConfig.colors.error : 'transparent'}`,
            backgroundColor: item.isRead ? 'transparent' : `${primary}08`,
            '&:hover': { backgroundColor: `${primary}10` },
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: accent,
              backgroundColor: `${accent}14`,
            }}
          >
            <Icon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: item.isRead ? 500 : 700,
                color: '#111827',
                lineHeight: 1.35,
                wordBreak: 'break-word',
              }}
            >
              {!item.isRead && <span className="sr-only">Non lue : </span>}
              {item.title}
            </Typography>
            {item.body && (
              <Typography
                sx={{
                  mt: 0.25,
                  fontSize: '0.8rem',
                  color: '#4B5563',
                  lineHeight: 1.45,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                }}
              >
                {item.body}
              </Typography>
            )}
            <Typography sx={{ mt: 0.5, fontSize: '0.72rem', color: '#9CA3AF' }}>
              {formatRelativeDate(item.createdAt)}
            </Typography>
          </Box>
          {!item.isRead && (
            <Box
              aria-hidden
              sx={{ width: 8, height: 8, mt: 0.75, borderRadius: '50%', flexShrink: 0, backgroundColor: primary }}
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  const renderBody = () => {
    if (!loaded) {
      if (loading) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} sx={{ color: primary }} />
          </Box>
        );
      }
      // échec silencieux : simple message, sans alerte
      return renderMessage(
        'Notifications indisponibles',
        'Vos notifications ne peuvent pas être affichées pour le moment. Réessayez un peu plus tard.',
        NotificationsOffOutlinedIcon,
      );
    }
    if (items.length === 0) {
      return isFiltered
        ? renderMessage(
          'Aucune notification pour ce filtre',
          'Essayez un autre filtre ou revenez à « Toutes ».',
          NotificationsNoneOutlinedIcon,
        )
        : renderMessage(
          'Aucune notification',
          'Les événements importants de votre compte s\'afficheront ici.',
          NotificationsNoneOutlinedIcon,
        );
    }
    return (
      <>
        {groups.map((group) => (
          <Box key={group.key} component="section" aria-label={group.label}>
            <Typography
              component="h3"
              sx={{
                px: 2,
                pt: 1.5,
                pb: 0.5,
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: '#9CA3AF',
              }}
            >
              {group.label}
            </Typography>
            <List disablePadding>{group.items.map(renderItem)}</List>
          </Box>
        ))}
        {hasMore && (
          <Box sx={{ p: 1 }}>
            <Button
              fullWidth
              onClick={loadMore}
              disabled={loadingMore}
              startIcon={loadingMore ? <CircularProgress size={14} sx={{ color: primary }} /> : undefined}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                color: primary,
                borderRadius: '10px',
                '&:hover': { backgroundColor: `${primary}0f` },
              }}
            >
              Voir plus
            </Button>
          </Box>
        )}
      </>
    );
  };

  return (
    <Box sx={{ '& .MuiTypography-root, & .MuiButton-root, & .MuiChip-label': { fontFamily: brandConfig.fontFamily } }}>
      {showFilters && <NotificationFilterChips notifications={notifications} />}
      {renderBody()}
    </Box>
  );
};

export default NotificationList;
